import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';

export const APP_VERSION = '2.5';
export const VERSION_DATE = '2026-05-04';
export const VERSION_NOTES = {
  en: [
    '🆕 Warehouse Manager Dashboard — dedicated dashboard for warehouse role (open actions, inventory, Cellcom orders)',
    '🆕 External Warehouse — upload pallet xlsb files, auto-match SI orders, track per-pallet stock',
    '🆕 Central Warehouse Withdrawals — generate pull orders for selected pallets, download Excel, fulfill/cancel',
    '🆕 User itamar (warehouse manager role) with restricted nav: Logistics + Warehouse + Cellcom Orders only',
    'External Warehouse + Pull Orders + QR Serials moved to Warehouse tab (QR also kept in Logistics)',
    'Stock — multi-row safe deduct/restore (no more multiplication on existing_stock)',
    'Stock — /cellcom-order endpoint now correctly deducts stock on order creation',
    'Stock — only zone="green" items restored on cancel (no phantom stock from yellow items)',
    'Dashboard — unified Profitability view: ALL non-cancelled orders count (no Future/Current split)',
    'Dashboard — Profit includes expected Q-Support 2% + Defective 1.75% + pending SO compensations',
    'Dashboard — Inventory Flow "Sold" KPI kept physical-only (shipped/completed)',
    'Dashboard — quarter-scoped M&C profit (was leaking across all time)',
    'Dashboard — defensive guards: skip qty<=0 and selling_price<=0 items',
    'Dashboard — credit distribution: equal split across quarter months when no income data',
    'Hebrew translations — M & C Profit drilldowns, Profit formula, profit breakdown rows',
    // ─── pre-existing v2.2 features ───
    'Customer Portal — full redesign with dashboard, financial cubes, payment tracking',
    'Customer Portal — price list with wish prices, RMA requests, search with arrival buckets',
    'Customer Portal — order status: Pending → Confirmed → Collecting → Delivered to FF → Shipped → Delivered',
    'Prices & Credits — deal prices auto-populate agreed prices (HL + 22K)',
    'Prices & Credits — per-deal closed badges (HL/22K), manual close/open',
    'Prices & Credits — credits export to show supplier what they owe',
    'Quarterly Credits — 4 clickable cubes: Support 4.5%, RMA 1.75%, HL Credit, 22K Deal',
    'Shipping Calculator — import + export cost per model, air vs ocean route selector',
    'Landed Costs — per-order overrides for insurance, warehouse, unload',
    'Warehouse — S&T / Shager / Total filter cubes with stock filtering',
    'Cellcom Orders — without pallets container calculation, ship out + arrival dates',
    'Logistics — tabs translated, payment due removed, containers by CBM',
    'Settings Pricing — lock/unlock with save, delete prices supported',
    'Excel 365 — direct sync from OneDrive (replaced Google Sheets)',
    'Dashboard — profit formula fixed: Income + Credits - COGS - Rebate',
    'Dashboard — Current shows only shipped+completed orders',
    'Dashboard — Pending Credits (SO) separated from Credits Received (SI)',
    'Dashboard — M & C Profit with per-order breakdown',
    'Dashboard — M% configurable per order (default 1.75%)',
    'Customer Portal — order status tracking (Pending → Confirmed → Collecting → Shipped → On the Way → Arrived → Delivered)',
    'Upload Center — new main tab for account manager with drag & drop',
    'Orders — Mark as Paid button with green PAID sticker',
    'Orders — Account manager can update shipping costs',
    'Orders — Due date only for SI (not SO)',
    'Air Shipping — option for flat sum or $/kg rate for M expenses',
    'Landed Costs — configurable insurance, warehouse, container fees',
    'S&T Stock — no shipping costs added (cost is all-in)',
    'Barcode — added to all Excel exports',
    'Bug fixes — 16 bugs found and fixed across the system',
  ],
  he: [
    '🆕 דשבורד מנהל מחסן — דשבורד ייעודי לrole warehouse (פעולות פתוחות, מלאי, הזמנות Cellcom)',
    '🆕 מחסן חיצוני — העלאת קבצי xlsb של משטחים, התאמה אוטומטית להזמנת SI, מעקב מלאי לפי משטח',
    '🆕 משיכות ממחסן מרכזי — יצירת הזמנת משיכה למשטחים נבחרים, הורדת אקסל, סימון כנמשך/בוטל',
    '🆕 משתמש itamar (role=warehouse) עם ניווט מצומצם: לוגיסטיקה + מחסן + הזמנות Cellcom בלבד',
    'מחסן חיצוני + משיכות + QR Serials עברו לטאב מחסן (QR נשאר גם בלוגיסטיקה)',
    'מלאי — multi-row safe deduct/restore (אין יותר כפל ב־existing_stock)',
    'מלאי — endpoint /cellcom-order מוריד מלאי בעת יצירת הזמנה',
    'מלאי — רק פריטי zone="green" מוחזרים בביטול (אין יותר phantom stock)',
    'דשבורד — רווחיות מאוחדת: כל ההזמנות הלא־מבוטלות נספרות (אין הפרדה Future/Current)',
    'דשבורד — Profit כולל תמיכה רבעונית 2% צפויה + תקולים 1.75% + פיצויים SO ממתינים',
    'דשבורד — KPI "נמכר" בתנועת המלאי נשאר פיזי בלבד (shipped/completed)',
    'דשבורד — רווח M&C מסונן לרבעון (היה דולף על פני כל הזמן)',
    'דשבורד — תוספות הגנתיות: דילוג על פריטים qty<=0 או selling_price<=0',
    'דשבורד — פיזור זיכויים: חלוקה שווה על חודשי הרבעון כשאין הכנסות',
    'תרגום עברית — drilldowns של M & C Profit, נוסחת רווח, פירוט שורות',
    // ─── תכונות v2.2 קיימות ───
    'פורטל לקוח — עיצוב מחדש עם דשבורד, קוביות פיננסיות, מעקב תשלומים',
    'פורטל לקוח — מחירון עם מחיר מבוקש, בקשות RMA, חיפוש עם טווחי הגעה',
    'פורטל לקוח — סטטוס: ממתין → אושר → נאסף → נמסר ל-FF → נשלח → נמסר',
    'מחירים וזיכויים — מחירי דילים מתמלאים אוטומטית (HL + 22K)',
    'מחירים וזיכויים — תגי סגירה לכל דיל, סגירה/פתיחה ידנית',
    'מחירים וזיכויים — ייצוא זיכויים להצגה לספק',
    'זיכויים רבעוניים — 4 קוביות: תמיכה 4.5%, RMA 1.75%, HL, 22K',
    'מחשבון שילוח — עלות ייבוא + ייצוא למודל, בחירת מסלול אוויר/ים',
    'עלויות נלוות — דריסה ידנית לכל הזמנה: ביטוח, מחסן, פריקה',
    'מחסן — קוביות סינון S&T / שאגר / סה"כ',
    'הזמנות סלקום — חישוב מכולות ללא משטחים, תאריך שילוח + הגעה',
    'לוגיסטיקה — טאבים מתורגמים, מכולות לפי CBM',
    'תמחור — נעילה/פתיחה עם שמירה, מחיקת מחירים',
    'אקסל 365 — סנכרון ישיר מ-OneDrive (החליף Google Sheets)',
    'דשבורד — נוסחת רווח תוקנה: הכנסות + זיכויים - עלות - ריבייט',
    'דשבורד — נוכחי מציג רק הזמנות שנשלחו/הושלמו',
    'דשבורד — זיכויים ממתינים (SO) מופרדים מזיכויים שהתקבלו (SI)',
    'דשבורד — רווח M & C עם פירוט להזמנה',
    'דשבורד — אחוז M ניתן לשינוי בכל הזמנה (ברירת מחדל 1.75%)',
    'פורטל לקוח — מעקב סטטוס הזמנה (ממתין → אושר → נאסף → נשלח → בדרך → הגיע → נמסר)',
    'מרכז העלאות — טאב ראשי חדש למנהלת חשבונות עם גרירה ושחרור',
    'הזמנות — כפתור סימון כשולם עם מדבקת PAID ירוקה',
    'הזמנות — מנהלת חשבונות יכולה לעדכן עלויות משלוח',
    'הזמנות — תאריך תשלום רק ל-SI (לא ל-SO)',
    'משלוח אוויר — אפשרות לסכום קבוע או $/ק"ג להוצאות M',
    'עלויות נלוות — ביטוח, מחסן, פריקת מכולות — ניתן להגדרה',
    'מלאי S&T — ללא עלויות משלוח (מחיר כולל)',
    'ברקוד — נוסף לכל ייצוא אקסל',
    'תיקוני באגים — 16 באגים נמצאו ותוקנו במערכת',
    'מערכת מיילים — שליחה אוטומטית לרואת חשבון על עלויות משלוח',
    'הזמנות עתידיות — הצגת מוקצה מול זמין',
    'משלוח אוויר — ספירת קרטונים ומשקל מדויק',
    'ברקוד — מוצג בכל העמודים',
    'תאריך תשלום — מוצג בכותרת ההזמנה',
    'הפחתת כמות — החזרת יחידות למלאי',
    'העברת פריטים — בין הזמנות סלקום',
    'השלמת הזמנות — סימון כהושלם, אפשרות לפתוח מחדש',
  ],
};

export default function Header() {
  const { t, i18n } = useTranslation();
  const { user, logout, setLang } = useAuth();
  const isCustomer = user?.role === 'customer';

  const toggleLang = async () => {
    const next = i18n.language === 'en' ? 'he' : 'en';
    await i18n.changeLanguage(next);
    localStorage.setItem('lang', next);
    document.dir = next === 'he' ? 'rtl' : 'ltr';
    try { await setLang(next); } catch {}
  };

  const isInt = typeof window !== 'undefined' && window.location.hostname.startsWith('int.');

  // Customer: clean header with no mention of Shager or Marshall
  if (isCustomer) {
    return (
      <>
      {isInt && (
        <div style={{ background: 'linear-gradient(90deg, #f97316, #fb923c)', color: '#fff', padding: '6px 16px', textAlign: 'center', fontSize: 13, fontWeight: 700, letterSpacing: 1, position: 'sticky', top: 0, zIndex: 9998 }}>
          ⚠️ INTEGRATION — TEST DATA ONLY ⚠️
        </div>
      )}
      <header className="header">
        <div className="header-brand">
          <h1>CUSTOMER PORTAL</h1>
        </div>
        <div className="header-center" />
        <div className="header-right">
          <button className="lang-btn" onClick={toggleLang}>
            {i18n.language === 'en' ? 'HE' : 'EN'}
          </button>
          <div className="user-badge">
            <span>{user?.full_name || user?.username}</span>
          </div>
          <button className="logout-btn" onClick={logout}>{t('auth.logout')}</button>
        </div>
      </header>
      </>
    );
  }

  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const lastSeen = localStorage.getItem('lastSeenVersion');
  const isNew = lastSeen !== APP_VERSION;

  useEffect(() => {
    if (isNew) setShowWhatsNew(true);
  }, [isNew]);

  const dismissWhatsNew = () => {
    setShowWhatsNew(false);
    localStorage.setItem('lastSeenVersion', APP_VERSION);
  };

  return (
    <>
    {isInt && (
      <div style={{
        background: 'linear-gradient(90deg, #f97316, #fb923c)',
        color: '#fff',
        padding: '6px 16px',
        textAlign: 'center',
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 1,
        position: 'sticky',
        top: 0,
        zIndex: 9998,
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      }}>
        ⚠️ INTEGRATION ENVIRONMENT — לא נתונים אמיתיים · אין שליחה ללקוחות · אין סנכרון מ-Marshall ⚠️
      </div>
    )}
    {showWhatsNew && (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        onClick={dismissWhatsNew}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 0, maxWidth: 500, width: '90%', maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
          onClick={e => e.stopPropagation()}>
          <div style={{ background: '#1565c0', color: '#fff', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>What's New in V{APP_VERSION}</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>{VERSION_DATE}</div>
            </div>
            <button onClick={dismissWhatsNew} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ padding: '16px 24px', maxHeight: '60vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => i18n.language !== 'en' && i18n.changeLanguage('en')}
                style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  background: i18n.language === 'en' ? '#1565c0' : '#fff', color: i18n.language === 'en' ? '#fff' : '#64748b' }}>EN</button>
              <button onClick={() => i18n.language !== 'he' && i18n.changeLanguage('he')}
                style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  background: i18n.language === 'he' ? '#1565c0' : '#fff', color: i18n.language === 'he' ? '#fff' : '#64748b' }}>עב</button>
            </div>
            {(VERSION_NOTES[i18n.language] || VERSION_NOTES.en).map((note, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#374151', direction: i18n.language === 'he' ? 'rtl' : 'ltr' }}>
                <span style={{ color: '#1565c0', fontWeight: 700 }}>•</span>
                <span>{note}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '12px 24px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
            <button onClick={dismissWhatsNew} style={{ background: '#1565c0', color: '#fff', border: 'none', padding: '10px 32px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Got it!
            </button>
          </div>
        </div>
      </div>
    )}
    <header className="header">
      <div className="header-brand">
        <h1>{t('app.title')}</h1>
        <span>{t('app.subtitle')}</span>
        <span onClick={() => setShowWhatsNew(true)} style={{ fontSize: 10, color: '#1565c0', background: '#eff6ff', padding: '1px 6px', borderRadius: 4, marginLeft: 8, cursor: 'pointer', fontWeight: 600 }}>V{APP_VERSION}</span>
      </div>
      <div className="header-center" />
      <div className="header-right">
        <button className="lang-btn" onClick={toggleLang}>
          {i18n.language === 'en' ? 'HE' : 'EN'}
        </button>
        <div className="user-badge">
          <span>{user?.full_name || user?.username}</span>
          <span className="role">{user?.role}</span>
        </div>
        <button className="logout-btn" onClick={logout}>{t('auth.logout')}</button>
      </div>
    </header>
    </>
  );
}
